import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Toasts } from './components/Toasts';
import { queryClient } from './lib/queryClient';
import { AuthGate, GuestOnly } from './features/auth/AuthGate';
import { DiscordDoneScreen } from './features/auth/DiscordDoneScreen';
import { LoginScreen } from './features/auth/LoginScreen';
import { RegisterScreen } from './features/auth/RegisterScreen';
import { ResetPasswordScreen } from './features/auth/ResetPasswordScreen';
import { ChannelSettings } from './features/channel-settings/ChannelSettings';
import { extraGlobals, extraRoutes } from './features/extras';
import { ServerSettings } from './features/server-settings/ServerSettings';
import { InviteScreen } from './features/servers/InviteScreen';
import { UserSettings } from './features/settings/UserSettings';
import { AppShell, ReadyGate } from './features/shell/AppShell';
import { ChannelRoute, DmRoute, NotFoundPane, ServerIndexRedirect } from './features/shell/ChannelView';
import { ThemeManager } from './features/theme/ThemeManager';
import { DmHome } from './features/dms/DmHome';
import { SuperReactionLayer } from './features/messages/SuperReactionLayer';

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <AuthGate>
      <ReadyGate>{children}</ReadyGate>
    </AuthGate>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/channels/@me" replace />} />
      <Route path="/login" element={<GuestOnly><LoginScreen /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><RegisterScreen /></GuestOnly>} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/auth/discord/done" element={<DiscordDoneScreen />} />
      <Route path="/invite/:code" element={<InviteScreen />} />
      <Route path="/settings/:tab?" element={<Protected><UserSettings /></Protected>} />
      <Route path="/servers/:serverId/settings/:tab?" element={<Protected><ServerSettings /></Protected>} />
      <Route path="/channel-settings/:channelId/:tab?" element={<Protected><ChannelSettings /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/channels/@me" element={<DmHome />} />
        <Route path="/channels/@me/:channelId" element={<DmRoute />} />
        <Route path="/channels/:serverId" element={<ServerIndexRedirect />} />
        <Route path="/channels/:serverId/:channelId" element={<ChannelRoute />} />
        {extraRoutes.map((r) => (
          <Route key={r.path} path={r.path} element={<r.element />} />
        ))}
        <Route path="*" element={<NotFoundPane text="Diese Seite gibt es nicht." showNav />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeManager />
        <AppRoutes />
        <Toasts />
        <SuperReactionLayer />
        {extraGlobals.map((G, i) => (
          <G key={i} />
        ))}
      </BrowserRouter>
    </QueryClientProvider>
  );
}
